import {
  StreamingDownsampler,
  TARGET_SAMPLE_RATE,
  UtteranceMeta,
  UtteranceSegmenter,
  UtteranceSegmenterOptions,
} from "./audio"

export type SpeechCaptureState =
  | "idle"
  | "requesting"
  | "listening"
  | "paused"
  | "permission-denied"
  | "unsupported"
  | "failed"

export type SpeechCaptureProfile = "dictation" | "conversation"

// voiceIsolation is standardized but is not present in the repository's
// current TypeScript DOM declarations yet. Browsers ignore unknown ideal
// constraints, so the extension remains safe on older implementations.
interface SpeechMediaTrackConstraints extends MediaTrackConstraints {
  voiceIsolation?: boolean
}

interface SpeechCaptureProfileDefaults {
  audioConstraints: SpeechMediaTrackConstraints
  segmenter: Omit<UtteranceSegmenterOptions, "onUtterance">
}

const SPEECH_CAPTURE_PROFILES: Record<SpeechCaptureProfile, SpeechCaptureProfileDefaults> = {
  dictation: {
    audioConstraints: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      // Bluetooth headset microphones can expose a quiet raw signal and rely
      // on the browser's speech gain stage. The adaptive gate follows the
      // resulting noise floor, so preserving audible speech wins here.
      autoGainControl: true,
      // Dictation has one nearby speaker, so prefer platform voice isolation
      // where it exists. This is an ideal constraint and may not be granted.
      voiceIsolation: true,
    },
    segmenter: {
      hangoverMs: 900,
      // Dictation has no final full-audio pass to repair arbitrary word seams.
      // Natural pauses still close quickly; six seconds improves delivery
      // latency while creating fewer irreversible seams than Conversation.
      maxUtteranceMs: 6000,
    },
  },
  conversation: {
    audioConstraints: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      // Do not request voiceIsolation: a platform implementation may treat
      // the second person across the room as background sound.
    },
    segmenter: {
      hangoverMs: 800,
      maxUtteranceMs: 3000,
    },
  },
}

export interface SpeechCaptureOptions {
  profile: SpeechCaptureProfile
  onUtterance: (samples: Float32Array, meta: UtteranceMeta) => void
  onLevel?: (rms: number) => void
  onStateChange?: (state: SpeechCaptureState, error?: unknown) => void
  audioConstraints?: MediaTrackConstraints
  segmenter?: Omit<UtteranceSegmenterOptions, "onUtterance">
}

export interface StopCaptureOptions {
  flush?: boolean
}

// Batch render quanta before crossing the worklet boundary, and transfer the
// resulting buffer instead of cloning it. Eight 128-sample quanta are about
// 21 ms at 48 kHz: small enough for the level meter and endpoint detector.
const WORKLET_PROCESSOR_SOURCE = `
class SpeechCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.frames = []
    this.sampleCount = 0
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0]
    if (channel && channel.length > 0) {
      const copy = new Float32Array(channel)
      this.frames.push(copy)
      this.sampleCount += copy.length
      if (this.frames.length >= 8) this.emitBatch()
    }
    return true
  }

  emitBatch() {
    if (this.sampleCount === 0) return
    const batch = new Float32Array(this.sampleCount)
    let offset = 0
    for (const frame of this.frames) {
      batch.set(frame, offset)
      offset += frame.length
    }
    this.frames = []
    this.sampleCount = 0
    this.port.postMessage(batch, [batch.buffer])
  }
}
registerProcessor("speech-capture", SpeechCaptureProcessor)
`

/**
 * Headless browser microphone capture. Consumers own controls, labels, and
 * persistence; this class owns permission, the WebAudio graph, resampling,
 * endpoint detection, and deterministic teardown.
 */
export class SpeechCapture {
  private readonly options: SpeechCaptureOptions
  private currentState: SpeechCaptureState = "idle"
  private generation = 0
  private clockMs = 0
  private mediaStream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private workletNode: AudioWorkletNode | null = null
  private scriptNode: ScriptProcessorNode | null = null
  private workletBlobUrl: string | null = null
  private segmenter: UtteranceSegmenter | null = null
  private downsampler: StreamingDownsampler | null = null
  private readonly trackEnded = () => {
    this.failUnexpectedCapture(new Error("Microphone input ended"))
  }
  private readonly contextStateChanged = () => {
    if (this.currentState !== "listening" || this.audioContext?.state === "running") return

    this.failUnexpectedCapture(new Error("Audio context stopped running"))
  }

  constructor(options: SpeechCaptureOptions) {
    this.options = options
  }

  get state(): SpeechCaptureState {
    return this.currentState
  }

  get elapsedMs(): number {
    return this.clockMs
  }

  async start(): Promise<void> {
    if (this.currentState === "requesting" || this.currentState === "listening") return
    await this.openMicrophone()
  }

  pause({ flush = true }: StopCaptureOptions = {}): void {
    if (flush) this.segmenter?.flush()
    this.generation += 1
    this.transition("paused")
    this.teardownAudio()
  }

  async resume(): Promise<void> {
    if (this.currentState === "listening" || this.currentState === "requesting") return
    await this.openMicrophone()
  }

  stop({ flush = true }: StopCaptureOptions = {}): void {
    if (flush) this.segmenter?.flush()
    this.generation += 1
    this.transition("idle")
    this.teardownAudio()
  }

  private async openMicrophone(): Promise<void> {
    const mediaDevices = navigator.mediaDevices
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

    if (!mediaDevices?.getUserMedia || !AudioContextClass) {
      this.transition("unsupported")
      return
    }

    const generation = ++this.generation
    this.teardownAudio()
    this.transition("requesting")

    const stream = await this.requestStream(mediaDevices, generation)
    if (!stream) return

    if (this.superseded(generation)) {
      stopTracks(stream)
      return
    }

    this.mediaStream = stream
    stream.getTracks().forEach((track) => track.addEventListener?.("ended", this.trackEnded))
    await this.openAudioGraph(AudioContextClass, stream, generation)
  }

  // Resolves to null when the request failed or was superseded; the caller
  // stops either way, so the state transition happens here.
  private async requestStream(mediaDevices: MediaDevices, generation: number): Promise<MediaStream | null> {
    try {
      return await mediaDevices.getUserMedia({
        audio: this.options.audioConstraints ?? SPEECH_CAPTURE_PROFILES[this.options.profile].audioConstraints,
      })
    } catch (error) {
      if (generation === this.generation) {
        this.transition(isPermissionError(error) ? "permission-denied" : "failed", error)
      }
      return null
    }
  }

  private async openAudioGraph(
    AudioContextClass: typeof AudioContext,
    stream: MediaStream,
    generation: number,
  ): Promise<void> {
    try {
      const context = new AudioContextClass()
      this.audioContext = context
      if (context.sampleRate < TARGET_SAMPLE_RATE) {
        this.teardownAudio()
        this.transition("unsupported")
        return
      }
      if (context.state === "suspended") await context.resume()
      if (context.state === "suspended") throw new Error("Audio context remained suspended")
      if (this.abandonIfSuperseded(generation)) return

      this.segmenter = new UtteranceSegmenter({
        ...SPEECH_CAPTURE_PROFILES[this.options.profile].segmenter,
        ...this.options.segmenter,
        onUtterance: this.options.onUtterance,
      })
      this.downsampler = new StreamingDownsampler(context.sampleRate || TARGET_SAMPLE_RATE)
      this.sourceNode = context.createMediaStreamSource(stream)
      await this.attachProcessor(context)
      if (this.abandonIfSuperseded(generation)) return

      context.addEventListener?.("statechange", this.contextStateChanged)
      this.transition("listening")
    } catch (error) {
      if (generation !== this.generation) return
      this.teardownAudio()
      this.transition("failed", error)
    }
  }

  // Worklets keep audio off the main thread; ScriptProcessor is the fallback
  // for browsers without one, and for a worklet that fails to load.
  private async attachProcessor(context: AudioContext) {
    if (!context.audioWorklet) {
      this.startScriptProcessor(context)
      return
    }
    try {
      await this.startWorklet(context)
    } catch {
      this.teardownWorklet()
      this.startScriptProcessor(context)
    }
  }

  // A newer start()/stop() has raced past this one, so its graph is garbage.
  private superseded(generation: number): boolean {
    return generation !== this.generation || this.currentState !== "requesting"
  }

  private abandonIfSuperseded(generation: number): boolean {
    if (!this.superseded(generation)) return false

    this.teardownAudio()
    return true
  }

  private async startWorklet(context: AudioContext) {
    const blob = new Blob([WORKLET_PROCESSOR_SOURCE], { type: "application/javascript" })
    this.workletBlobUrl = URL.createObjectURL(blob)
    await context.audioWorklet.addModule(this.workletBlobUrl)
    const node = new AudioWorkletNode(context, "speech-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
    })
    node.port.onmessage = (event: MessageEvent<Float32Array>) => this.handleFrame(event.data)
    this.workletNode = node
    this.sourceNode?.connect(node)
  }

  private startScriptProcessor(context: AudioContext) {
    const node = context.createScriptProcessor(4096, 1, 1)
    node.onaudioprocess = (event: AudioProcessingEvent) => {
      this.handleFrame(new Float32Array(event.inputBuffer.getChannelData(0)))
    }
    this.sourceNode?.connect(node)
    node.connect(context.destination)
    this.scriptNode = node
  }

  private handleFrame(frame: Float32Array) {
    if (this.currentState !== "listening" || !this.audioContext || !this.segmenter || !this.downsampler) return
    const inputRate = this.audioContext.sampleRate || TARGET_SAMPLE_RATE
    const downsampled = this.downsampler.push(frame)
    if (downsampled.length > 0) this.segmenter.push(downsampled, this.clockMs)
    this.clockMs += (frame.length / inputRate) * 1000
    this.options.onLevel?.(frameRms(frame))
  }

  private teardownAudio() {
    this.teardownWorklet()
    if (this.scriptNode) {
      this.scriptNode.onaudioprocess = null
      safely(() => this.scriptNode?.disconnect())
      this.scriptNode = null
    }
    if (this.sourceNode) {
      safely(() => this.sourceNode?.disconnect())
      this.sourceNode = null
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.removeEventListener?.("ended", this.trackEnded))
      stopTracks(this.mediaStream)
      this.mediaStream = null
    }
    if (this.audioContext) {
      const context = this.audioContext
      context.removeEventListener?.("statechange", this.contextStateChanged)
      safely(() => {
        const closing = context.close()
        closing?.catch(() => undefined)
      })
      this.audioContext = null
    }
    this.segmenter = null
    this.downsampler = null
  }

  private failUnexpectedCapture(error: Error) {
    if (this.currentState !== "requesting" && this.currentState !== "listening") return

    this.generation += 1
    this.teardownAudio()
    this.transition("failed", error)
  }

  private teardownWorklet() {
    if (this.workletNode) {
      this.workletNode.port.onmessage = null
      safely(() => this.workletNode?.port.close())
      safely(() => this.workletNode?.disconnect())
      this.workletNode = null
    }
    if (this.workletBlobUrl) {
      URL.revokeObjectURL(this.workletBlobUrl)
      this.workletBlobUrl = null
    }
  }

  private transition(state: SpeechCaptureState, error?: unknown) {
    this.currentState = state
    this.options.onStateChange?.(state, error)
  }
}

function stopTracks(stream: MediaStream) {
  stream.getTracks().forEach((track) => track.stop())
}

function safely(callback: () => void) {
  try {
    callback()
  } catch {
    // WebAudio nodes throw when a browser has already torn their graph down.
  }
}

function isPermissionError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "NotAllowedError" || error.name === "SecurityError"
    : true
}

function frameRms(frame: Float32Array): number {
  if (frame.length === 0) return 0
  let sum = 0
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i]
  return Math.sqrt(sum / frame.length)
}
